import { useState } from 'react';
import { B, FONT } from '../lib/constants';
import { useFullWeather, windDirection } from '../hooks/useWeather';

export default function WeatherWidget({ address, projectId }) {
  const { weather, loading } = useFullWeather(address, projectId);
  const [selectedDay, setSelectedDay] = useState(null);

  if (loading) return (
    <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}` }}>
      <div style={{ color: B.muted, fontSize: 12 }}>Loading weather…</div>
    </div>
  );

  if (!weather) return null;

  const { current, forecast, location, nwsUrl } = weather;
  const day = selectedDay !== null ? forecast[selectedDay] : null;

  return (
    <div style={{ background: B.navy, borderRadius: 14, marginBottom: 12, border: `1px solid ${B.slate}`, overflow: 'hidden', fontFamily: FONT }}>

      {/* Current Conditions */}
      <div style={{ padding: '16px 18px 12px', borderBottom: `1px solid ${B.slate}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>Current Conditions</div>
            <div style={{ color: B.muted, fontSize: 11, marginBottom: 8 }}>{location}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <div style={{ fontSize: 52, lineHeight: 1 }}>{current.icon}</div>
              <div>
                <div style={{ color: B.white, fontSize: 42, fontWeight: 900, lineHeight: 1 }}>{current.temp}°</div>
                <div style={{ color: B.muted, fontSize: 12, marginTop: 2 }}>{current.description}</div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: 6 }}>
              <div style={{ color: B.muted, fontSize: 10 }}>FEELS LIKE</div>
              <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>{current.feelsLike}°</div>
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ color: B.muted, fontSize: 10 }}>HUMIDITY</div>
              <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>{current.humidity}%</div>
            </div>
            <div>
              <div style={{ color: B.muted, fontSize: 10 }}>WIND</div>
              <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>{current.wind} mph {windDirection(current.windDir)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 7-Day Forecast Strip */}
      <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', borderBottom: selectedDay !== null ? `1px solid ${B.slate}` : 'none' }}>
        {forecast.map((d, i) => (
          <button key={i} onClick={() => setSelectedDay(selectedDay === i ? null : i)}
            style={{
              flex: '0 0 auto', minWidth: 72, padding: '12px 8px', background: selectedDay === i ? B.mid : 'transparent',
              border: 'none', borderRight: `1px solid ${B.slate}`, cursor: 'pointer', fontFamily: FONT,
              transition: 'background 0.15s',
            }}>
            <div style={{ color: selectedDay === i ? B.goldlt : B.muted, fontSize: 10, fontWeight: 700, marginBottom: 4 }}>{d.dayName}</div>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{d.icon}</div>
            <div style={{ color: B.white, fontSize: 13, fontWeight: 700 }}>{d.hi}°</div>
            <div style={{ color: B.muted, fontSize: 11 }}>{d.lo}°</div>
            {d.rain > 20 && <div style={{ color: '#64B5F6', fontSize: 9, marginTop: 2 }}>💧{d.rain}%</div>}
          </button>
        ))}
      </div>

      {/* Day Detail Panel */}
      {day && (
        <div style={{ padding: '14px 18px', background: B.mid }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ color: B.white, fontSize: 15, fontWeight: 700 }}>{day.fullDate}</div>
              <div style={{ color: B.muted, fontSize: 12 }}>{day.description}</div>
            </div>
            <div style={{ fontSize: 32 }}>{day.icon}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            {[
              { l: 'High', v: `${day.hi}°F` },
              { l: 'Low', v: `${day.lo}°F` },
              { l: 'Rain Chance', v: `${day.rain}%` },
              { l: 'Max Wind', v: `${day.wind} mph` },
              { l: 'Sunrise', v: day.sunrise },
              { l: 'Sunset', v: day.sunset },
            ].map(s => (
              <div key={s.l} style={{ background: B.slate, borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ color: B.muted, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>{s.l}</div>
                <div style={{ color: B.white, fontSize: 14, fontWeight: 700, marginTop: 2 }}>{s.v}</div>
              </div>
            ))}
          </div>
          <a href={nwsUrl} target="_blank" rel="noreferrer"
            style={{ display: 'block', background: B.gold, color: B.white, borderRadius: 8, padding: '9px', textAlign: 'center', textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
            Full NWS Forecast →
          </a>
        </div>
      )}
    </div>
  );
}
